import { useSearchParams } from 'react-router-dom'
import { SortingState } from '@tanstack/react-table'
import { isEqual } from 'lodash'
import { useEffect, useMemo, useState } from 'react'
import { matchPath } from 'react-router-dom'
import { QuickCreateTaskModal } from 'app/components/tasks/QuickCreateTaskModal'
import { FilterObject, PredefinedFilter, convertUrlFiltersToES } from 'app/filter.utils'
import { useListTraversal } from 'app/hooks/useListTraversal'
import { useSidebarCounts } from 'app/hooks/useSidebarCounts'
import { ContactJson } from 'app/types/entities'
import { CustomPageType } from 'app/types/entities'
import { UserJson } from 'app/types/entities'
import { showModal } from 'app/lib/modal'
import { notifyError } from 'app/services/notify.client'
import { getFilterableColumns } from 'app/table-columns/tasks'
import { useUser } from 'app/utils'
import { bg } from 'lib/background'
import { debug } from 'lib/logging'
import { ObjectId } from 'lib/ObjectId'
import { Jsonify } from 'lib/toJSON'
import { useLocation } from 'react-router-dom'
import { gqlFetchList } from 'app/lib/graphqlClient'

export type TasksData = {
  slug: string
  groupBy: string
  urlFilters: string | null
  preAppliedFilters: Jsonify<PredefinedFilter>
  initialFilters?: FilterObject[]
  joiner: string
  activeSubpage: string
  subpages?: CustomPageType[]
  /** A tab-owned clause the filter bar cannot express: My Tasks' owner-or-role OR. */
  baseSearch?: string
}

type TaskTraversalItem = { taskId: string; contactId: string }

type Group = {
  id: string
  groupName: string
  query: string
  initialSort: { sortingState: SortingState; sortString: string }
}

function useTaskTraversalDerivations(params: {
  drawerTaskTraversalItemsByGroup: Record<string, TaskTraversalItem[]>
  activeTraversalGroup: string | undefined
  searchParams: URLSearchParams
}) {
  const { drawerTaskTraversalItemsByGroup, activeTraversalGroup, searchParams } = params
  const groupPriority = useMemo(() => [`Overdue`, `Today`, `Upcoming`], [])

  const highlightTaskId = useMemo(() => searchParams.get(`highlightTaskId`) ?? undefined, [searchParams])
  const drawerContactIdFromUrl = useMemo(() => searchParams.get(`drawer`) ?? undefined, [searchParams])

  const derivedLookup = useMemo(() => {
    const taskIdToGroup: Record<string, string> = {}
    const contactIdToGroups: Record<string, Set<string>> = {}
    for (const [groupName, items] of Object.entries(drawerTaskTraversalItemsByGroup)) {
      for (const item of items) {
        if (item.taskId) taskIdToGroup[item.taskId] = groupName
        if (item.contactId) {
          const existing = contactIdToGroups[item.contactId] ?? new Set<string>()
          existing.add(groupName)
          contactIdToGroups[item.contactId] = existing
        }
      }
    }
    return { taskIdToGroup, contactIdToGroups }
  }, [drawerTaskTraversalItemsByGroup])

  const taskDeepLinkTraversalGroup = useMemo(
    () => (highlightTaskId ? (derivedLookup.taskIdToGroup[highlightTaskId] ?? undefined) : undefined),
    [derivedLookup, highlightTaskId],
  )

  const contactDeepLinkTraversalGroup = useMemo(() => {
    if (!drawerContactIdFromUrl) return undefined
    const groupsForContact = derivedLookup.contactIdToGroups[drawerContactIdFromUrl]
    if (!groupsForContact) return undefined
    for (const g of groupPriority) {
      if (groupsForContact.has(g)) return g
    }
    return [...groupsForContact][0]
  }, [derivedLookup, drawerContactIdFromUrl, groupPriority])

  const effectiveTraversalGroup = taskDeepLinkTraversalGroup ?? activeTraversalGroup ?? contactDeepLinkTraversalGroup

  const drawerTaskTraversalItems = useMemo(() => {
    if (!effectiveTraversalGroup) return []
    return (drawerTaskTraversalItemsByGroup[effectiveTraversalGroup] ?? []).filter(
      (item) => item.taskId && item.contactId,
    )
  }, [drawerTaskTraversalItemsByGroup, effectiveTraversalGroup])

  return { highlightTaskId, drawerContactIdFromUrl, effectiveTraversalGroup, drawerTaskTraversalItems }
}

export type UseTasksReturn = {
  me: ReturnType<typeof useUser>
  data: TasksData
  searchParams: URLSearchParams
  setSearchParams: ReturnType<typeof useSearchParams>[1]
  syncedColumnSizing: Record<string, number> | undefined
  openContactDrawer: boolean
  focusedContact: ContactJson | undefined
  groups: Group[] | undefined
  sort: { sortingState: SortingState; sortString: string } | undefined
  filterableColumns: ReturnType<typeof getFilterableColumns>
  totalItems: number | undefined
  uniqueKey: string
  refreshTrigger: number
  groupBy: string
  highlightTaskId: string | undefined
  drawerTaskTraversal: ReturnType<typeof useListTraversal<{ id?: string }>>
  onTaskTraversalItemsChangeByGroup: Record<string, (items: TaskTraversalItem[]) => void>
  onContactClickByGroup: Record<string, (contactId: string, highlightTaskId?: string) => void>
  handleTaskCreatedOrUpdated: () => Promise<void>
  handleColumnSizingChange: (sizing: Record<string, number>) => void
  handleSortChange: (sortingState: SortingState, sortString: string) => void
  handleGroupByChange: (incomingGroupBy: string) => void
  handleSearchChange: (search: string) => void
  onTotalChange: (groupName: string, total: number) => void
  handleContactDrawerClose: () => void
  handleContactChange: (contact: ContactJson) => void
  openAddNewTaskModal: () => void
}

export function useTasks(data: TasksData): UseTasksReturn {
  const me = useUser()

  const [searchParams, setSearchParams] = useSearchParams()
  const [syncedColumnSizing, setSyncedColumnSizing] = useState<Record<string, number> | undefined>(undefined)
  const [openContactDrawer, setOpenContactDrawer] = useState(false)
  const [focusedContact, setFocusedContact] = useState<ContactJson>()
  const [drawerTaskTraversalItemsByGroup, setDrawerTaskTraversalItemsByGroup] = useState<
    Record<string, TaskTraversalItem[]>
  >({})
  const [activeTraversalGroup, setActiveTraversalGroup] = useState<string | undefined>()

  const preAppliedFilters = useMemo(() => data.preAppliedFilters, [data.preAppliedFilters])
  const urlFilters = useMemo(() => data.urlFilters, [data.urlFilters])
  const defaultJoin = useMemo(() => data.joiner, [data.joiner])
  const groupBy = useMemo(() => data.groupBy, [data.groupBy])
  const searchValue = useMemo(() => searchParams.get(`search`) || ``, [searchParams])

  const [groups, setGroups] = useState<Group[]>()
  const [sort, setSort] = useState<{ sortingState: SortingState; sortString: string }>()
  const [totals, setTotals] = useState<Record<string, number> | undefined>(undefined)
  const [uniqueKey, setUniqueKey] = useState(ObjectId())
  const [refreshTrigger, setRefreshTrigger] = useState(0)

  const location = useLocation()
  const isLoading = false
  const { refresh: refreshSidebarCounts } = useSidebarCounts()

  const filterableColumns = useMemo(() => getFilterableColumns(preAppliedFilters), [preAppliedFilters])
  const totalItems = totals ? Object.values(totals).reduce((acc, total) => acc + total, 0) : undefined

  const onTaskTraversalItemsChangeByGroup = useMemo(() => {
    const handlers: Record<string, (items: TaskTraversalItem[]) => void> = {}
    for (const group of groups ?? []) {
      handlers[group.groupName] = (items) =>
        setDrawerTaskTraversalItemsByGroup((prev) => ({ ...prev, [group.groupName]: items }))
    }
    return handlers
  }, [groups])

  const { highlightTaskId, drawerTaskTraversalItems } = useTaskTraversalDerivations({
    drawerTaskTraversalItemsByGroup,
    activeTraversalGroup,
    searchParams,
  })

  const handleContactClick = (contactId: string, incomingHighlightTaskId?: string) => {
    bg(async () => {
      let groupName: string | undefined
      for (const [gName, items] of Object.entries(drawerTaskTraversalItemsByGroup)) {
        if (
          incomingHighlightTaskId
            ? items.some((i) => i.taskId === incomingHighlightTaskId)
            : items.some((i) => i.contactId === contactId)
        ) {
          groupName = gName
          break
        }
      }
      if (groupName) setActiveTraversalGroup(groupName)

      const defaultHighlightTaskId =
        incomingHighlightTaskId ??
        drawerTaskTraversalItemsByGroup[groupName ?? ``]?.find((i) => i.contactId === contactId)?.taskId

      setSearchParams((prev) => {
        prev.set(`drawer`, contactId)
        if (defaultHighlightTaskId) prev.set(`highlightTaskId`, defaultHighlightTaskId)
        else prev.delete(`highlightTaskId`)
        return prev
      })
      setFocusedContact(undefined)
      setOpenContactDrawer(true)
      // The backend `contact(id)` resolver currently throws
      // INTERNAL_SERVER_ERROR for many contacts that actually exist, which
      // makes contactStore.fetchOne return null and the drawer never opens.
      // Sidestep by querying the contacts list with an id filter — the list
      // resolver doesn't have the bug and returns the full shape (the
      // `contacts` MODEL_FIELDS string mirrors `contact`'s fields).
      const { hits } = await gqlFetchList<ContactJson>(`contacts`, {
        search: `id:${contactId}`,
        pagination: { page: 0, limit: 1 },
      })
      const result = hits?.[0]
      if (!result) {
        notifyError(`Contact not found`)
        setFocusedContact(undefined)
        setOpenContactDrawer(false)
      } else {
        setFocusedContact(result)
        setOpenContactDrawer(true)
      }
    })
  }

  const handleTaskClick = (taskId: string) => {
    const item = drawerTaskTraversalItems.find((i) => i.taskId === taskId)
    if (!item) return
    handleContactClick(item.contactId, item.taskId)
  }

  const drawerTaskTraversal = useListTraversal<{ id?: string }>(
    drawerTaskTraversalItems.map((i) => ({ id: i.taskId })),
    highlightTaskId ? { id: highlightTaskId } : undefined,
    (item) => {
      if (item.id) handleTaskClick(item.id)
    },
  )

  const onContactClickByGroup = useMemo(() => {
    const handlers: Record<string, (contactId: string, highlightTaskId?: string) => void> = {}
    for (const group of groups ?? []) {
      handlers[group.groupName] = (contactId, highlightTaskId) => {
        setActiveTraversalGroup(group.groupName)
        handleContactClick(contactId, highlightTaskId)
      }
    }
    return handlers
  }, [groups, handleContactClick])

  const createGroups = async () => {
    setTotals(undefined)
    const terms = convertUrlFiltersToES(urlFilters, defaultJoin)
    const termsString = terms.length ? terms.join(` AND `) : ``
    const searchString = searchValue || ``
    // Join only the non-empty clauses with ` AND `. Prepending ` AND ` to a
    // clause without checking the left side is present produced a query that
    // started with a dangling ` AND ` (e.g. ` AND farmer`) whenever the user
    // searched with no active filters, which OpenSearch's query_string parser
    // rejects with a 400 and leaves the task list empty (TRK-5754).
    const andJoin = (...clauses: Array<string | undefined>) => clauses.filter(Boolean).join(` AND `)
    switch (groupBy) {
      case `due-date`:
        setGroups([
          {
            id: ObjectId(),
            groupName: `Overdue`,
            query: andJoin(data.baseSearch, `dueDate:[* TO now/d}`, termsString, searchString),
            initialSort: { sortingState: [{ id: `Due Date`, desc: true }], sortString: `dueDate:desc` },
          },
          {
            id: ObjectId(),
            groupName: `Today`,
            query: andJoin(data.baseSearch, `dueDate:[now/d TO now/d]`, termsString, searchString),
            initialSort: { sortingState: [{ id: `Due Date`, desc: false }], sortString: `dueDate:asc` },
          },
          {
            id: ObjectId(),
            groupName: `Upcoming`,
            query: andJoin(data.baseSearch, `dueDate:{now/d TO *]`, termsString, searchString),
            initialSort: { sortingState: [{ id: `Due Date`, desc: false }], sortString: `dueDate:asc` },
          },
        ])
        break
      case `owner`: {
        const result = await gqlFetchList<UserJson>('users', { search: `type:staff` }, 'id name')
        const owners = result.hits
        setGroups(
          owners?.map((owner) => ({
            id: ObjectId(),
            groupName: `${owner.name}`,
            query: andJoin(data.baseSearch, owner.id ? `ownerId:${owner.id}` : `!ownerId:*`, termsString, searchString),
            initialSort: { sortingState: [{ id: `Owner`, desc: false }], sortString: `dueDate:asc` },
          })),
        )
        break
      }
      case `none`:
        setGroups([
          {
            id: ObjectId(),
            groupName: `All`,
            query: andJoin(data.baseSearch, termsString, searchString),
            initialSort: { sortingState: [{ id: `Due Date`, desc: true }], sortString: `dueDate:desc` },
          },
        ])
        break
    }
  }

  const handleTaskCreatedOrUpdated = async () => {
    setRefreshTrigger((prev) => prev + 1)
    refreshSidebarCounts()
  }

  const handleContactDrawerClose = () => {
    setOpenContactDrawer(false)
    setActiveTraversalGroup(undefined)
    setSearchParams((prev) => {
      prev.delete(`drawer`)
      prev.delete(`highlightTaskId`)
      return prev
    })
  }

  const openAddNewTaskModal = () => {
    showModal(<QuickCreateTaskModal onTaskCreatedOrUpdated={handleTaskCreatedOrUpdated} />, {
      // `hideTitle: true` — the QuickCreateTaskModal renders the title input
      // itself as the visual header (and its own close X), so we don't want
      // the legacy Modal chrome stacking another header on top.
      hideTitle: true,
      isFullScreen: false,
      overflowHidden: false,
      removePadding: true,
      useRadix: true,
    })
  }

  useEffect(() => {
    if (isLoading) setGroups(undefined)
  }, [isLoading])
  useEffect(() => {
    createGroups().catch((err) => debug(`tasks`, `createGroups error`, err))
  }, [urlFilters, defaultJoin, groupBy, searchValue, data.baseSearch])
  useEffect(() => {
    setUniqueKey(ObjectId())
  }, [groups])
  useEffect(() => {
    const contactId = searchParams.get(`drawer`)
    if (contactId && !focusedContact) handleContactClick(contactId)
  }, [searchParams])

  return {
    me,
    data,
    searchParams,
    setSearchParams,
    syncedColumnSizing,
    openContactDrawer,
    focusedContact,
    groups,
    sort,
    filterableColumns,
    totalItems,
    uniqueKey,
    refreshTrigger,
    groupBy,
    highlightTaskId,
    drawerTaskTraversal,
    onTaskTraversalItemsChangeByGroup,
    onContactClickByGroup,
    handleTaskCreatedOrUpdated,
    handleColumnSizingChange: (sizing) => setSyncedColumnSizing(sizing),
    handleSortChange: (sortingState, sortString) => setSort({ sortingState, sortString }),
    handleGroupByChange: (incomingGroupBy) => {
      if (incomingGroupBy === groupBy) return
      setSearchParams((prev) => {
        prev.set(`groupBy`, incomingGroupBy)
        return prev
      })
    },
    handleSearchChange: (search) => {
      setSearchParams((prev) => {
        if (search) prev.set(`search`, search)
        else prev.delete(`search`)
        return prev
      })
    },
    onTotalChange: (groupName, total) => setTotals((prev) => ({ ...prev, [groupName]: total })),
    handleContactDrawerClose,
    handleContactChange: (contact) => setFocusedContact(contact),
    openAddNewTaskModal,
  }
}
